<template>
  <div class="space-y-4">
    <div class="grid grid-cols-1 gap-4 sm:grid-cols-2">
      <BaseCard class="relative">
        <SetChangeResetPasswordForm
          :require-current-password="hasPassword"
          user-type="merchant-group-admin"
          @submit="onSubmit"
        />
        <template v-if="isSubmitting">
          <BaseInnerLoading />
        </template>
      </BaseCard>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ApiError } from "@/api"
import { useMerchantGroupAdmin } from "@/composables/merchantGroup/useMerchantGroupAdmin"
import type { SubmissionContext } from "vee-validate"

const router = useRouter()
const { setPageTitle } = usePageTitle()
const merchantGroupAdmin = useMerchantGroupAdmin()
const hasPassword = computed(
  () => merchantGroupAdmin.value?.hasPassword ?? false
)
const pageTitle = computed(() =>
  hasPassword.value ? "Change Password" : "Set Password"
)
watchEffect(() => setPageTitle(pageTitle.value))

const isSubmitting = ref(false)

const onSubmit = async (
  params: SetChangeResetPasswordFormOutput,
  context: SubmissionContext<SetChangeResetPasswordFormInput>
) => {
  isSubmitting.value = true
  try {
    await changeMerchantGroupAdminPassword(params)
    toast.success(
      hasPassword.value
        ? "Password updated"
        : "Password set"
    )
    router.push({ name: "merchantGroupDashboard" })
  } catch (error) {
    if (error instanceof ApiError) {
      if (
        error.code &&
        [
          "invalid-password",
          "same-as-current-password",
        ].includes(error.code)
      ) {
        context.setFieldError(
          "password",
          error.detail ||
            "Password does not meet requirements"
        )
        return
      }

      if (
        error.code &&
        [
          "current-password-required",
          "invalid-current-password",
        ].includes(error.code)
      ) {
        context.setFieldError(
          "currentPassword",
          error.detail ?? "Current password is invalid"
        )
        return
      }
    }

    logError(error)
    toast.error("Failed to update password")
  } finally {
    isSubmitting.value = false
  }
}
</script>
